import * as React from 'react';
import { Modal, Button } from 'react-bootstrap';

// -----------------------------------------------------------------------------
// "What's New" feed
// -----------------------------------------------------------------------------
// This is the ONLY file you edit to announce a new feature. You do NOT need to
// re-record the full demo (that lives in demo-video-modal.tsx and only changes
// on a major redesign).
//
// To publish an update after shipping a feature:
//   1. Record a short (~30-60s) clip in Loom, or upload one to YouTube.
//   2. Grab the video id from its share URL:
//        Loom    https://www.loom.com/share/<ID>       -> loom('<ID>')
//        YouTube https://www.youtube.com/watch?v=<ID>  -> youtube('<ID>')
//   3. Add a new entry to the TOP of WHATS_NEW_ITEMS below, with a unique `id`.
//
// Every practitioner who hasn't seen that `id` yet gets it auto-popped once on
// their next login, then it stops. They can always re-watch from Help > What's New.
// -----------------------------------------------------------------------------

export type WhatsNewItem = {
  // Unique + stable. This is the per-user "seen" key — never reuse an old id, and
  // changing an existing id re-shows that item to everyone.
  id: string;
  title: string;
  date: string; // e.g. 'August 2026' — shown as a small caption under the title
  src: string;  // embed URL; build it with loom(...) or youtube(...)
};

export const loom = (id: string) => `https://www.loom.com/embed/${id}`;
export const youtube = (id: string) => `https://www.youtube.com/embed/${id}`;

// Newest first. Leave empty and nothing pops up. Add the first real entry after
// you've recorded the Results Overview + Notes clip — for example:
//
//   {
//     id: 'results-overview-notes-redesign-2026-07',
//     title: 'Redesigned Results Overview & Notes tab',
//     date: 'July 2026',
//     src: loom('paste-the-loom-id-here'),
//   },
//
export const WHATS_NEW_ITEMS: WhatsNewItem[] = [];

type Props = {
  show: boolean;
  items: WhatsNewItem[];
  onHide: () => void;
};

export class WhatsNewModal extends React.Component<Props> {
  render() {
    const { show, items, onHide } = this.props;
    if (!show) {
      return null;
    }

    return (
      <Modal show={show} onHide={onHide} size="lg" scrollable>
        <Modal.Header closeButton style={{ backgroundColor: '#337ab7', color: '#fff' }}>
          <Modal.Title>What&rsquo;s New in LabSmarts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {items.length === 0 ? (
            <p>You&rsquo;re all caught up.</p>
          ) : (
            items.map((item, i) => (
              <div key={item.id} style={{ marginBottom: i < items.length - 1 ? 32 : 0 }}>
                <h5 style={{ marginBottom: 2 }}>{item.title}</h5>
                <div style={{ color: '#766d66', fontSize: 13, marginBottom: 10 }}>{item.date}</div>
                {/* Responsive 16:9 embed */}
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                  <iframe
                    title={item.title}
                    src={item.src}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
